/* 화면(MCP 관제). mathcore helper.js 로 math2d 를 띄운다. 이 페이지는 모델을 부르지 않는다.
 * 상주 페이지(worker/session.ts 가 여는 같은 화면, ?role=worker)가 엔진(run.ts)을 돌리고, 호스트 모델이 MCP 도구(pipeline_next, pipeline_submit)로 답한다.
 * 사람이 보는 페이지는 문항을 넣어 파이프라인을 시작하고(/api/pipeline/start) 상주 페이지의 요청, 기록, 판정, 그림을 폴링해 보여 준다(/api/pipeline/view) */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import katex from 'katex';
import { drawCalls } from './tools';
import type { MathcoreApp } from './tools';
import { createPipeline } from './pipeline';
import { createPipeline3d } from './pipeline3d';
import { allObjects3d } from './tools/executor3d';
import type { Math3DView } from './style3d';
import type { EntryKind, Meta } from './run';
import { readMeasurements } from './measure';
import { VARIANT_ELEMENTS } from './solution';
import { applyCsatDefaults, fitCamera, rememberMountEpsilon, waitForCurves } from './style';
import { ArchiveView } from './archive';
import { STARTERS, StarterModal, type StarterKind } from './starter';

const MODULES: mathcore.MathCoreModuleNames[] = ['math2d', 'plugin.ui_math2d'];

const QUERY = new URLSearchParams(location.search);
// 상주 페이지(워커)는 자기 자신을 관제하지 않는다
const ROLE = QUERY.get('role') ?? '';
// worker(상주 페이지)와 test(도구 시험)는 자기 그림판에서 엔진을 돌린다. 그 밖(관제 화면)은 상주 페이지의 캡처를 보이고 자기 그림판을 숨긴다.
// 숨긴 그림판은 크기 0 이라 그 안의 엔진 계산(접점, 교점 탐색)이 깨진다(실측 2026-10-20: camera.width 0 → 접선 무효)
const LOCAL_CANVAS = ROLE === 'worker' || ROLE === 'test';
// space=3d 로 열린 페이지는 math2d 대신 math3d 를 띄운다. 어느 쪽을 쓸지는 문항을 시작하는 호스트 모델이 정한다(pipeline_start 의 space)
const SPACE_3D = QUERY.get('space') === '3d';

interface Entry {
  kind: EntryKind;
  text: string;
}

function waitFor(condition: () => boolean, timeout = 60000, interval = 150): Promise<void> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const check = () => {
      if (condition()) { resolve(); return; }
      if (Date.now() - started > timeout) { reject(new Error('시간 초과')); return; }
      setTimeout(check, interval);
    };
    check();
  });
}

const isReady = (instance: any) => Boolean(instance?.objectManager?.factory || instance?.commandManager?.execute);

async function mountMath3d(target: HTMLElement, apiKey: string): Promise<Math3DView> {
  await waitFor(() => typeof mathcore !== 'undefined' && Boolean(mathcore.helper));
  await mathcore.helper.load(['math3d'], { key: apiKey });
  await waitFor(() => Boolean((mathcore as any).math3d?.Math3D));
  const instance = new (mathcore as any).math3d.Math3D();
  instance.render(target);
  await new Promise((done) => { setTimeout(done, 1500); });
  return instance as Math3DView;
}

async function mountMath2d(target: HTMLElement, apiKey: string): Promise<MathcoreApp> {
  await waitFor(() => typeof mathcore !== 'undefined' && Boolean(mathcore.helper));
  await mathcore.helper.load(MODULES, { key: apiKey });
  const construct = () => (mathcore.math2d ? new mathcore.math2d.Math2D() : undefined);
  await waitFor(() => isReady(construct()));
  await new Promise((done) => { setTimeout(done, 300); });
  const instance: any = construct();
  if (typeof instance.init === 'function') { try { await instance.init(); } catch { /* init 이 필수가 아닌 도구도 있다 */ } }
  await instance.render(target);
  return instance as MathcoreApp;
}

// 캡처 전 카메라 맞춤. 맞춤 자체는 style.ts fitCamera(실행 직전에도 같은 함수를 넓게 쓴다)
const VIEW_FILL = 0.7;
async function fitView(app: MathcoreApp): Promise<void> {
  // 서버를 새로 띄운 직후 첫 실행은 곡선 표본이 늦게 온다(실측 2026-10-19: 표본 없이 맞추면 곡선이 화면 밖). 표본을 기다린 뒤 맞춘다
  await waitForCurves(app);
  // 표본은 카메라 창에 따라 단계적으로 넓어진다(실측: 한 번 더 맞춰도 극소 -25 가 -22 에서 끊김). epsilon 이 더 안 바뀔 때까지 반복한다(최대 4회)
  for (let pass = 0; pass < 4; pass += 1) {
    const before = [...(app.cameraManager?.epsilon ?? [])];
    fitCamera(app, VIEW_FILL);
    await new Promise<void>((done) => { setTimeout(done, 500); });
    const after = app.cameraManager?.epsilon ?? [];
    if (before.length && after.every((v, i) => Math.abs(v - before[i]) <= before[i] * 0.01)) break;
  }
  await new Promise<void>((done) => { setTimeout(done, 300); });
}

function entryClass(kind: EntryKind): string {
  switch (kind) {
    case 'call': return 'mc-chat__exec mc-chat__exec--call';
    case 'result': return 'mc-chat__exec mc-chat__exec--result';
    case 'error': return 'mc-chat__exec mc-chat__exec--error';
    case 'meta': return 'mc-chat__meta';
    case 'user': return 'mc-chat__bubble mc-chat__bubble--user';
    case 'think': return 'mc-chat__bubble mc-chat__bubble--think';
    default: return 'mc-chat__bubble';
  }
}

const KATEX_OPTIONS = { throwOnError: false, strict: false };
const MD_PLUGINS = { remark: [remarkGfm, remarkMath], rehype: [[rehypeKatex, KATEX_OPTIONS]] } as const;

/** 마크다운과 $…$ 수식을 그대로 그린다. 모델 출력을 고쳐 쓰지 않는다. 못 읽는 식은 KaTeX 가 빨갛게 보이고 그것이 모델의 결과다 */
function Markdown({ text }: { text: string }) {
  return <ReactMarkdown remarkPlugins={MD_PLUGINS.remark as never} rehypePlugins={MD_PLUGINS.rehype as never}>{text}</ReactMarkdown>;
}

/** 칩의 한 줄 글. $…$ 하나면 KaTeX 로, 아니면 글자 그대로 */
function MathText({ text }: { text: string }) {
  const t = text.trim();
  if (t.startsWith('$') && t.endsWith('$') && t.length > 2) return <span dangerouslySetInnerHTML={{ __html: katex.renderToString(t.slice(1, -1), KATEX_OPTIONS) }} />;
  return <span>{text}</span>;
}

/** 문항 패널. 내부 레퍼런스 데이터(출처, 번호, 정답)는 화면에 내지 않는다(사용자 지시 2026-10-14). 변형 문항은 검증을 통과한 것만 보이고, 불성립이면 사유만 보인다 */
function ProblemPanel({ meta, busy }: { meta: Meta; busy: boolean }) {
  const chips: { text: string; tone?: string }[] = [];
  if (meta.mode === 'variant') chips.push({ text: '변형 문항', tone: 'accent' });
  const empty = meta.mode === 'variant' ? (busy ? '변형 문항을 만들고 검증하는 중입니다. 통과한 문항만 여기에 보입니다.' : '변형 문항이 없습니다.') : '아직 문항이 없습니다. 오른쪽 입력창에 수능 문항을 넣으세요.';
  return (
    <div className="panel panel--problem">
      <div className="panel__head"><span>문항</span><span className="chips">{chips.map((c) => <span key={c.text} className={`chip${c.tone ? ` chip--${c.tone}` : ''}`}><MathText text={c.text} /></span>)}</span></div>
      <div className="panel__body problem__text">{meta.rejected ? <span className="muted">변형 문항 불성립: {meta.rejected}. 검증을 통과한 문항만 냅니다.</span> : meta.problem ? <Markdown text={meta.problem} /> : <span className="muted">{empty}</span>}</div>
    </div>
  );
}

/** 풀이 패널 — 탭: 정석 풀이(Track A) · 실전 풀이(Track B) · 그림 요약 · 원본→변형 · 모델 원문. 칩: 정답 · 검증 · 유사도 · 소요 시간 */
function SolutionPanel({ markdown, meta, busy, model }: { markdown: string; meta: Meta; busy: boolean; model: string }) {
  // 해설은 run.ts 가 '# 제목' 절로 고정해 낸다. 절 머리로 나눈다
  const sections = ('\n' + markdown).split('\n# ').slice(1).map((p) => { const nl = p.indexOf('\n'); return nl < 0 ? { title: p.trim(), body: '' } : { title: p.slice(0, nl).trim(), body: p.slice(nl + 1).trim() }; }).filter((s) => s.title && s.title !== '문제');
  const tabs = [...sections.map((s) => ({ key: s.title, label: s.title.replace(' (Track A)', ' · Track A').replace(' (Track B)', ' · Track B'), body: s.body })), ...(markdown.trim() ? [{ key: '__raw', label: '모델 원문', body: '' }] : [])];
  const [active, setActive] = useState<string>('');
  // 고른 탭이 없으면 정석 풀이 탭을 연다(사용자 지시 2026-10-15)
  const current = tabs.find((t) => t.key === active) ?? tabs.find((t) => t.key.startsWith('정석 풀이')) ?? tabs[0];
  const chips: { text: string; tone?: string }[] = [];
  if (meta.acceptance) chips.push({ text: `판정 ${meta.acceptance}`, tone: meta.acceptance === '성립' ? 'ok' : meta.acceptance === '불성립' ? 'bad' : 'warn' });
  if (meta.answer) chips.push({ text: `정답 ${meta.answer}`, tone: 'accent' });
  if (meta.verdict) chips.push({ text: meta.verdict, tone: meta.verdict.startsWith('✓') ? 'ok' : meta.verdict.startsWith('✗') ? 'bad' : undefined });
  if (meta.similarity !== undefined) chips.push({ text: `유사도 ${meta.similarity}%` });
  if (meta.changes?.length) chips.push({ text: `변형 ${meta.changes.length}건` });
  if (meta.elapsed) chips.push({ text: meta.elapsed });
  // AI 풀이는 100% 가 아니다(사용자 지적 2026-10-14). 풀이가 있는 동안 항상 보이는 경고
  if (markdown.trim()) chips.push({ text: 'AI 풀이 · 사람 확인 필요', tone: 'warn' });
  return (
    <div className="panel panel--solution">
      <div className="panel__head">
        <span className="tabs">{tabs.map((t) => <button key={t.key} type="button" className={current && current.key === t.key ? 'is-on' : undefined} onClick={() => setActive(t.key)}>{t.label}</button>)}{tabs.length === 0 ? <span>풀이</span> : null}</span>
        <span className="chips">{chips.map((c) => <span key={c.text} className={`chip${c.tone ? ` chip--${c.tone}` : ''}`}><MathText text={c.text} /></span>)}</span>
      </div>
      <div className="panel__body solution__body">
        {!markdown.trim() ? <div className="muted">{busy ? '풀이를 만드는 중입니다…' : '문항을 보내면 정석 풀이와 실전 풀이, 검증 결과가 여기에 나옵니다.'}</div>
          : current?.key === '__raw' ? <pre className="solution__raw">{markdown}</pre>
          : current ? <><div className="notice">풀이 단계는 사내 GPU 의 AI({model})가 쓴 것입니다. 코드가 대조하는 것은 정답과 도구가 잰 값뿐이며, 풀이 과정의 수식·논리는 검증되지 않았습니다. 틀릴 수 있습니다.</div><Markdown text={current.body} /></> : null}
        {meta.measured?.length && current && current.key !== '__raw' ? <div className="measured"><span className="muted">도구가 잰 값</span>{meta.measured.map((m) => <span key={m} className="chip">{m.startsWith('- ') ? m.slice(2) : m}</span>)}</div> : null}
      </div>
    </div>
  );
}
interface RemoteView {
  state: string;
  id?: number;
  label?: string;
  kind?: string;
  messages?: { role: string; content: string }[];
  stamp?: string;
  meta?: Meta;
  solution?: string;
  log?: string[];
  message?: string;
  screenshot?: string;
}

/** 진행 기록 한 줄 「[kind] 글」을 종류와 글로 가른다 */
function splitLog(line: string): { kind: EntryKind; text: string } {
  const close = line.indexOf('] ');
  if (!line.startsWith('[') || close < 0) return { kind: 'meta', text: line };
  return { kind: line.slice(1, close) as EntryKind, text: line.slice(close + 2) };
}

/** MCP 연결 방법. 절차는 regist-mcp 스킬과 docs/deploy.md 의 순서 그대로이고 주소는 이 페이지의 origin 으로 채운다 */
function ConnectGuide({ onClose }: { onClose: () => void }) {
  const origin = location.origin;
  const mcp = `${origin}/mcp`;
  const code = (text: string) => <code style={{ userSelect: 'all' }}>{text}</code>;
  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="panel modal__box">
        <div className="panel__head"><span>MCP 연결 방법</span><span className="muted">이 화면은 모델을 부르지 않는다. 연결된 호스트 모델이 그린다</span></div>
        <div className="panel__body modal__body" style={{ overflow: 'auto' }}>
          <div className="notice">서버 주소 {code(mcp)} · 토큰은 서버의 <code>.env</code>(또는 <code>.env.local</code>)의 <code>MCP_TOKEN</code>. 토큰 없이 부르면 401 이 정상(잠김)이다</div>
          <h4 style={{ margin: '8px 0 4px' }}>A. 이 PC 의 Claude Code(터미널)에 붙이기</h4>
          <ol style={{ margin: '0 0 10px 18px', lineHeight: 1.7 }}>
            <li>터미널: {code(`claude mcp add --transport http csat ${mcp} --header "Authorization: Bearer <토큰>"`)}</li>
            <li>확인: {code('claude mcp list')} 에 csat 이 연결됨으로 뜨고, 대화에서 {code('/mcp')} 를 치면 도구 10개(pipeline_start, pipeline_next, pipeline_submit, archive_subjects, archive_search, archive_item, verify_answer, read_scene, capture_screen, save_tool_data)가 보인다</li>
            <li>대화창에 문항을 붙이고 「csat 의 pipeline_start 로 시작하고, pipeline_next 가 주는 요청마다 pipeline_submit 으로 답해 끝까지 진행해. 끝 응답의 판정 줄과 그림을 보여 줘」라고 한다</li>
            <li>이 화면이 2초마다 상주 페이지를 읽어 요청, 진행 기록, 판정, 그림을 보여 준다. 또는 이 화면의 입력창으로 먼저 시작해 두고 호스트에게 「pipeline_next 로 받아 답해」라고 해도 된다</li>
          </ol>
          <h4 style={{ margin: '8px 0 4px' }}>B. claude.ai 사이트의 커스텀 커넥터로 붙이기</h4>
          <ol style={{ margin: '0 0 10px 18px', lineHeight: 1.7 }}>
            <li>접속은 상관의 브라우저가 아니라 Anthropic 클라우드(발신 대역 160.79.104.0/21)가 한다. 서버가 외부에서 열린 https 도메인이어야 하고 localhost 는 쓸 수 없다</li>
            <li>기존 커넥터가 있으면 삭제한다(인증 설정은 추가 뒤 고칠 수 없다)</li>
            <li>설정 → 커넥터 → 사용자 지정 커넥터 추가: 주소 {code('https://<도메인>/mcp')}, 로그인 없음, 헤더 이름은 목록의 {code('authorization')}(직접 만든 이름은 거부됨), 값은 {code('Bearer <토큰>')}(공백 한 칸, 따옴표 없음), 「필수」 켬</li>
            <li>추가 대화상자의 숫자: 연결 401 은 정상, .well-known 404 도 정상. 200 인데 ⊗ 이면 /mcp 가 화면 HTML 을 주는 구 빌드다</li>
            <li>대화창 + → 커넥터에서 켜고 「커넥터에 어떤 도구가 있어?」로 도구 10개를 확인한다</li>
            <li>안 되면 서버 로그 한 줄로 판단한다: {code('docker logs csat-mcp --tail 30 | grep "MCP 요청"')} → 통과, 값이 다름(길이 N), 자격증명 없음 중 하나가 찍힌다. 헤더가 끝내 안 되면 주소 끝에 {code('?token=<토큰>')} 을 붙이고 헤더는 비운다</li>
          </ol>
          <h4 style={{ margin: '8px 0 4px' }}>C. 개발 서버에 올리기(docs/deploy.md)</h4>
          <ol style={{ margin: '0 0 4px 18px', lineHeight: 1.7 }}>
            <li>개발 장비 {code('yarn build')}(web → mcp → server)</li>
            <li>서버 dev-deploy/ 에 server, mcp-server.mjs, deploy-web.sh, docker-compose.yml, image/Dockerfile 을 옮긴다</li>
            <li>서버 dev-deploy/.env 에 MCP_TOKEN, MATHCORE_API_BASE_URL, MATHCORE_API_VERSION, MATHCORE_API_KEY, HOST_PORT(권한 600)</li>
            <li>{code('cd dev-deploy && ./deploy-web.sh')} 뒤 {code('/healthz')} 가 mcp:true 인지 본다</li>
          </ol>
        </div>
        <div className="modal__foot">
          <span className="muted">연결이 없으면 이 화면은 파이프라인을 시작만 할 수 있고 그림은 서지 않는다</span>
          <span className="seg"><button type="button" onClick={onClose}>닫기</button></span>
        </div>
      </div>
    </div>
  );
}

function App() {
  const canvas = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const [app, setApp] = useState<MathcoreApp>();
  const [status, setStatus] = useState('도구 로드 중');
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [starting, setStarting] = useState(false);
  const [remote, setRemote] = useState<RemoteView>({ state: 'idle' });
  const [note, setNote] = useState('');
  const [view, setView] = useState<'work' | 'archive'>('work');
  const [starter, setStarter] = useState<StarterKind | null>(null);
  const [guide, setGuide] = useState(false);
  // 레퍼런스 03 「변형 요소 선택」. 비어 있으면 모델이 2~4개를 고른다
  const [elements, setElements] = useState<string[]>([]);
  // 시험 스크립트가 마지막 판정 meta 를 읽어 가는 다리
  const metaRef = useRef<Meta>({});
  useEffect(() => { (window as any).readMeta = () => metaRef.current; }, []);

  useEffect(() => {
    const key = document.querySelector('meta[name="mathcore-api-key"]')?.getAttribute('content') ?? '';
    if (SPACE_3D) {
      mountMath3d(canvas.current!, key)
        .then((instance) => {
          setApp(instance as unknown as MathcoreApp);
          (window as any).mathcoreTool = instance;
          (window as any).pipeline = createPipeline3d(instance);
          (window as any).readScene = () => allObjects3d(instance).map((o) => ({ id: o.id, name: o.name, type: o.type, category: o.category, isValid: o.isValid, position: o.position }));
          (window as any).saveToolData = () => JSON.stringify(allObjects3d(instance));
          (window as any).readMeasurements = () => allObjects3d(instance).filter((o) => o.type === 'measurement' && o.isValid !== false).map((o) => ({ name: o.name ?? '', kind: o.category ?? 'measurement', value: Number(o.value) }));
          setStatus('math3d 준비됨');
        })
        .catch((error: Error) => setStatus(`도구 로드 실패: ${error.message}`));
      return;
    }
    mountMath2d(canvas.current!, key)
      .then((instance) => {
        setApp(instance);
        rememberMountEpsilon(instance);
        applyCsatDefaults(instance);
        (window as any).mathcoreTool = instance;
        // MCP 입구(worker/session.ts 가 headless 크로미움으로 이 페이지를 열고 부른다). 엔진(run.ts)의 모델 포트는 호스트 모델이 응답하는 대기 포트다
        (window as any).pipeline = createPipeline(instance, () => fitView(instance));
        // 시험용 직접 입구: 엔진의 마지막 단계(대조와 실행)만 부른다. MCP 도구로는 노출하지 않는다
        (window as any).runCalls = (raw: unknown, append = false, given?: string[]) => drawCalls(instance, raw, append, given);
        (window as any).readScene = () => {
          const manager: any = (instance as any).objectManager;
          const objects: any[] = manager?.getAllObjects?.() ?? [];
          return objects
            .filter((o) => !String(o.name || '').startsWith('nametext_'))
            .map((o) => ({ id: o.id, name: o.name, type: o.type, category: o.category, isValid: o.isValid, position: o.position }));
        };
        (window as any).saveToolData = () => instance.dataManager?.saveAsString?.() ?? '';
        (window as any).readMeasurements = () => readMeasurements(instance);
        setStatus('math2d 준비됨');
      })
      .catch((error: Error) => setStatus(`도구 로드 실패: ${error.message}`));
  }, []);

  // 관제: 상주 페이지의 파이프라인 상태를 2초마다 받는다
  useEffect(() => {
    if (LOCAL_CANVAS) return undefined;
    let stopped = false;
    const tick = async () => {
      try {
        const response = await fetch('/api/pipeline/view', { cache: 'no-store' });
        const next = (await response.json()) as RemoteView;
        if (stopped) return;
        setRemote(next);
        if (next.meta) metaRef.current = next.meta;
      } catch (error) {
        if (!stopped) setNote(`관제 연결 실패: ${error instanceof Error ? error.message : String(error)}`);
      }
    };
    void tick();
    const timer = window.setInterval(() => { void tick(); }, 2000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    const element = body.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [remote.log?.length]);

  const start = useCallback(async (problem: string, kind: StarterKind = 'original') => {
    if (!problem.trim() || starting) return;
    setStarting(true);
    setNote('');
    try {
      const response = await fetch('/api/pipeline/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ problem, variantElements: kind === 'variant' ? elements : undefined }) });
      const result = (await response.json()) as { ok?: boolean; error?: string };
      if (result.ok) { setText(''); setNote('시작했습니다. 연결된 MCP 호스트 모델이 pipeline_next 로 요청을 받아 pipeline_submit 으로 답하면 아래에 진행이 쌓입니다.'); }
      else setNote(`시작 실패: ${result.error ?? response.status}`);
    } catch (error) {
      setNote(`시작 실패: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setStarting(false);
    }
  }, [elements, starting]);

  const meta = remote.meta ?? {};
  const running = remote.state === 'ask' || remote.state === 'wait';
  const askHead = remote.state === 'ask' ? (remote.messages?.[0]?.content ?? '').slice(0, 400) : '';
  const stateText = remote.state === 'idle' ? '대기' : remote.state === 'ask' ? `요청 ${remote.id} 대기 중 · ${remote.label ?? ''}` : remote.state === 'wait' ? '엔진 실행 중' : remote.state === 'done' ? `끝 · ${remote.stamp ?? ''}` : remote.state === 'error' ? `오류 · ${remote.message ?? ''}` : remote.state;

  return (
    <div className={LOCAL_CANVAS ? 'app app--worker' : 'app'}>
      <header className="topbar">
        <div className="topbar__brand">수능 수학 그림 도우미<span className="topbar__sub">math2d · MCP 관제 · 호스트 모델이 그린다</span></div>
        <div className="topbar__modes">
          <span className="seg">
            <button type="button" className={view === 'work' ? 'is-on' : undefined} onClick={() => setView('work')}>작업</button>
            <button type="button" className={view === 'archive' ? 'is-on' : undefined} onClick={() => setView('archive')}>기출 탐색</button>
          </span>
          <span className="seg">
            <button type="button" className="mcp-guide" onClick={() => setGuide(true)}>MCP 연결 방법</button>
          </span>
          {view === 'work' ? (
            <span className="seg">
              {VARIANT_ELEMENTS.map((e) => (
                <button key={e} type="button" className={elements.includes(e) ? 'is-on' : undefined} disabled={starting} onClick={() => setElements((list) => (list.includes(e) ? list.filter((x) => x !== e) : [...list, e]))}>{e}</button>
              ))}
            </span>
          ) : null}
        </div>
        <div className="topbar__status"><span className="status-text">{status}</span><span className="topbar__elapsed">{stateText}</span></div>
      </header>
      {view === 'archive' ? <ArchiveView /> : null}
      {guide ? <ConnectGuide onClose={() => setGuide(false)} /> : null}
      {starter ? (
        <StarterModal
          kind={starter}
          onClose={() => setStarter(null)}
          onRun={(problem) => {
            const kind = starter;
            setStarter(null);
            setView('work');
            void start(problem, kind);
          }}
        />
      ) : null}
      {/* math2d 캔버스가 마운트된 채 남아야 하므로 작업 화면은 지우지 않고 숨긴다 */}
      <div className="main" style={view === 'work' ? undefined : { display: 'none' }}>
        <section className="work">
          <ProblemPanel meta={meta} busy={running} />
          <div className="panel panel--canvas">
            <div className="panel__head"><span>그림 · math2d{ROLE === 'worker' ? '' : ' (상주 페이지의 화면)'}</span><span className="muted">{meta.objectCount !== undefined ? `개체 ${meta.objectCount}개${meta.failureCount ? ` · 실패 ${meta.failureCount}` : ''}` : ''}</span></div>
            {/* 상주 페이지의 그림. 이 페이지의 캔버스는 워커가 쓰는 엔진 자리이므로 사람에게는 상주 페이지 화면을 보인다 */}
            {LOCAL_CANVAS || !remote.screenshot ? null : <img className="remote-shot" alt="상주 페이지 화면" src={`data:image/png;base64,${remote.screenshot}`} style={{ width: '100%', display: 'block' }} />}
            <div className="canvas" ref={canvas} style={LOCAL_CANVAS || !remote.screenshot ? undefined : { display: 'none' }} />
          </div>
          <SolutionPanel markdown={remote.solution ?? ''} meta={meta} busy={running} model="MCP 호스트 모델" />
        </section>
        <aside className="log">
          <div className="mc-chat">
            <div className="panel__head"><span>파이프라인</span><span className="muted">{stateText}</span></div>
            <div className="mc-chat__body" ref={body}>
              {note ? <div className="mc-chat__bubble mc-chat__bubble--meta">{note}</div> : null}
              {remote.state === 'idle' && !(remote.log?.length) ? <div className="log__empty">문항을 넣어 시작하면 상주 페이지의 엔진이 풀이 → 값 옮기기 → 입력 개체 → 구성 → 측정 순서로 호스트 모델에게 묻고, 그 기록이 여기 쌓입니다.</div> : null}
              {(remote.log ?? []).map((line, index) => { const e = splitLog(line); return <div key={index} className={entryClass(e.kind)}>{e.text}</div>; })}
              {remote.state === 'ask' ? (
                <div className="mc-chat__bubble mc-chat__bubble--meta">
                  <div>요청 {remote.id} · {remote.label} · {remote.kind === 'json' ? 'JSON' : '글'}로 답한다. 호스트 모델이 pipeline_next 로 받아 pipeline_submit 으로 답합니다.</div>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0', fontSize: '12px' }}>{askHead}{askHead.length >= 400 ? ' …' : ''}</pre>
                </div>
              ) : null}
              {running ? <div className="mc-chat__loading"><span /><span /><span /></div> : null}
            </div>
            <div className="mc-chat__footer">
              <div className="chips starter">
                {STARTERS.map((s) => <button key={s.kind} type="button" className="chip chip--accent" disabled={starting || !app} onClick={() => setStarter(s.kind)}>{s.label}</button>)}
              </div>
              <div className={focused ? 'mc-chat__input-wrap is-focused' : 'mc-chat__input-wrap'}>
                <div className="mc-chat__input-area">
                  <textarea
                    className="mc-chat__textarea"
                    value={text}
                    rows={3}
                    placeholder="수능 문항 원문(LaTeX 가능)을 붙여 넣고 시작"
                    disabled={!app}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onChange={(event) => setText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                        event.preventDefault();
                        void start(text);
                      }
                    }}
                  />
                  <button type="button" className="mc-chat__send" disabled={!text.trim() || !app || starting} onClick={() => void start(text)}>↑</button>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
